import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Check, Loader2, PhoneCall, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const FILTERS = [
  { key: "pendiente", label: "Pendientes" },
  { key: "all", label: "Todas" },
];

/**
 * AdminContactRequests — the «Te llamamos» requests sent from the home. Each
 * one also goes out by email (Ajustes → Notificaciones); this list is where
 * the team marks who has already been called.
 */
export default function AdminContactRequests() {
  const [filter, setFilter] = useState("pendiente");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(
    async (skip = 0) => {
      const { data } = await api.get("/admin/contact-requests", {
        params: { status: filter, limit: PAGE_SIZE, skip },
      });
      setItems((prev) => (skip === 0 ? data.items : [...prev, ...data.items]));
      setTotal(data.total);
      setPending(data.pending);
    },
    [filter],
  );

  useEffect(() => {
    setLoading(true);
    load(0)
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await load(items.length);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoadingMore(false);
    }
  };

  const setStatus = async (row, status) => {
    setSavingId(row.id);
    try {
      await api.patch(`/admin/contact-requests/${row.id}`, { status });
      toast.success(status === "atendida" ? "Marcada como atendida" : "Vuelve a estar pendiente");
      await load(0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl text-brand-ink flex items-center gap-2">
          <PhoneCall className="w-7 h-7" />
          Te llamamos
        </h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Solicitudes que dejan los visitantes en el formulario «Te llamamos» de la home. Cada una
          llega también por email a los destinatarios de Ajustes → Notificaciones por email. Cuando
          hayas llamado, márcala como atendida.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="contact-filter">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={
              "text-xs px-3 py-1.5 rounded-full border transition-colors " +
              (filter === f.key
                ? "bg-brand text-white border-brand"
                : "bg-white text-gray-700 border-gray-200 hover:border-brand/40")
            }
            data-testid={`contact-filter-${f.key}`}
          >
            {f.label}
            {f.key === "pendiente" && (
              <span className="ml-1.5 text-[10px] opacity-70 font-mono-data">{pending}</span>
            )}
          </button>
        ))}
      </div>

      <Card className="border border-gray-200 shadow-none rounded-lg overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-gray-400">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-gray-400" data-testid="contact-empty">
            {filter === "pendiente" ? "No hay solicitudes pendientes." : "Aún no ha llegado ninguna solicitud."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
                  <th className="text-left px-4 py-2.5">Fecha</th>
                  <th className="text-left px-4 py-2.5">Nombre</th>
                  <th className="text-left px-4 py-2.5">Teléfono</th>
                  <th className="text-left px-4 py-2.5">Email</th>
                  <th className="text-left px-4 py-2.5">Consulta</th>
                  <th className="text-left px-4 py-2.5">Estado</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const done = r.status === "atendida";
                  return (
                    <tr
                      key={r.id}
                      className={`border-b border-gray-100 align-top hover:bg-gray-50 ${done ? "opacity-60" : ""}`}
                      data-testid={`contact-row-${r.id}`}
                    >
                      <td className="px-4 py-2.5 text-xs text-gray-600 font-mono-data whitespace-nowrap">
                        {fmtDateTime(r.created_at)}
                      </td>
                      <td className="px-4 py-2.5 font-medium text-brand-ink whitespace-nowrap">
                        {r.first_name} {r.last_name}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <a href={`tel:${r.phone}`} className="font-mono-data text-brand-ink hover:text-brand">
                          {r.phone}
                        </a>
                      </td>
                      <td className="px-4 py-2.5">
                        <a href={`mailto:${r.email}`} className="text-brand-ink hover:text-brand break-all">
                          {r.email}
                        </a>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700 max-w-md whitespace-pre-wrap">
                        {r.message || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                        {done ? (
                          <span title={r.handled_by ? `Por ${r.handled_by}` : undefined}>
                            <span className="inline-flex px-1.5 py-0.5 rounded border text-emerald-700 bg-emerald-50 border-emerald-200 font-mono-data text-[10px]">
                              ATENDIDA
                            </span>
                            <span className="block text-gray-500 mt-1">{fmtDateTime(r.handled_at)}</span>
                          </span>
                        ) : (
                          <span className="inline-flex px-1.5 py-0.5 rounded border text-amber-800 bg-amber-50 border-amber-200 font-mono-data text-[10px]">
                            PENDIENTE
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <Button
                          variant={done ? "ghost" : "outline"}
                          size="sm"
                          disabled={savingId === r.id}
                          onClick={() => setStatus(r, done ? "pendiente" : "atendida")}
                          data-testid={`contact-toggle-${r.id}`}
                        >
                          {savingId === r.id ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                          ) : done ? (
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                          ) : (
                            <Check className="w-3.5 h-3.5 mr-1" />
                          )}
                          {done ? "Reabrir" : "Marcar atendida"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {!loading && items.length < total && (
        <div className="flex justify-center mt-4">
          <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
            {loadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Cargar más
          </Button>
        </div>
      )}
    </div>
  );
}
